import { useCallback, useEffect, useRef, useState } from 'react'
import { Superpose } from './Superpose'

/**
 * Photo en plein écran avec zoom.
 *  - Téléphone : pincer pour zoomer, glisser un doigt pour se déplacer,
 *    toucher deux fois pour zoomer / revenir.
 *  - Ordinateur : molette de la souris (zoom vers le pointeur), glisser pour
 *    se déplacer, double-clic pour zoomer / revenir.
 *  - Partout : boutons − / + / « Ajuster » et touche Échap pour fermer.
 */
const MIN = 1
const MAX = 8

export function VisionneusePhoto({
  url,
  fermer,
  precedente,
  suivante,
  position
}: {
  url: string
  fermer: () => void
  precedente?: () => void
  suivante?: () => void
  position?: { rang: number; total: number }
}) {
  const [vue, setVue] = useState({ z: 1, x: 0, y: 0 })
  const cadre = useRef<HTMLDivElement>(null)
  const pointeurs = useRef(new Map<number, { x: number; y: number }>())
  const geste = useRef<{ dist: number; z: number; cx: number; cy: number; x: number; y: number } | null>(null)
  const dernierToucher = useRef(0)
  const depart = useRef<{ x: number; y: number } | null>(null)
  // Nouvelle photo : on repart en vue complète.
  useEffect(() => setVue({ z: 1, x: 0, y: 0 }), [url])

  /** Zoome vers un point de l'écran (le point sous le doigt reste en place). */
  const zoomerVers = useCallback((nz: number, px: number, py: number) => {
    setVue((v) => {
      const z = Math.min(MAX, Math.max(MIN, nz))
      if (z === MIN) return { z: 1, x: 0, y: 0 }
      const r = cadre.current?.getBoundingClientRect()
      if (!r) return { ...v, z }
      // Coordonnées du point par rapport au centre du cadre.
      const cx = px - r.left - r.width / 2
      const cy = py - r.top - r.height / 2
      const k = z / v.z
      return { z, x: cx - (cx - v.x) * k, y: cy - (cy - v.y) * k }
    })
  }, [])

  const centre = () => {
    const r = cadre.current?.getBoundingClientRect()
    return r ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : { x: 0, y: 0 }
  }

  useEffect(() => {
    const t = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fermer()
      if (e.key === '+' || e.key === '=') zoomerVers(vue.z * 1.5, centre().x, centre().y)
      if (e.key === '-') zoomerVers(vue.z / 1.5, centre().x, centre().y)
    }
    window.addEventListener('keydown', t)
    return () => window.removeEventListener('keydown', t)
  }, [fermer, zoomerVers, vue.z])

  // Molette : écouteur non passif pour empêcher le défilement de la page.
  useEffect(() => {
    const el = cadre.current
    if (!el) return
    const roue = (e: WheelEvent) => {
      e.preventDefault()
      zoomerVers(vueRef.current.z * Math.exp(-e.deltaY * 0.0022), e.clientX, e.clientY)
    }
    el.addEventListener('wheel', roue, { passive: false })
    return () => el.removeEventListener('wheel', roue)
  }, [zoomerVers])

  const vueRef = useRef(vue)
  vueRef.current = vue

  function bas(e: React.PointerEvent) {
    ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
    pointeurs.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    const pts = [...pointeurs.current.values()]
    const v = vueRef.current
    depart.current = pts.length === 1 ? { x: e.clientX, y: e.clientY } : null
    if (pts.length === 2) {
      geste.current = {
        dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y),
        z: v.z,
        cx: (pts[0].x + pts[1].x) / 2,
        cy: (pts[0].y + pts[1].y) / 2,
        x: v.x,
        y: v.y
      }
    } else if (pts.length === 1) {
      geste.current = { dist: 0, z: v.z, cx: e.clientX, cy: e.clientY, x: v.x, y: v.y }
      // Double toucher (téléphone).
      if (e.pointerType === 'touch') {
        const t = Date.now()
        if (t - dernierToucher.current < 300) basculer(e.clientX, e.clientY)
        dernierToucher.current = t
      }
    }
  }

  function bouge(e: React.PointerEvent) {
    if (!pointeurs.current.has(e.pointerId)) return
    pointeurs.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    const g = geste.current
    if (!g) return
    const pts = [...pointeurs.current.values()]
    if (pts.length >= 2 && g.dist > 0) {
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y)
      const r = cadre.current!.getBoundingClientRect()
      const z = Math.min(MAX, Math.max(MIN, g.z * (dist / g.dist)))
      const cx = g.cx - r.left - r.width / 2
      const cy = g.cy - r.top - r.height / 2
      const mx = (pts[0].x + pts[1].x) / 2 - g.cx
      const my = (pts[0].y + pts[1].y) / 2 - g.cy
      const k = z / g.z
      setVue(z === 1 ? { z: 1, x: 0, y: 0 } : { z, x: cx - (cx - g.x) * k + mx, y: cy - (cy - g.y) * k + my })
    } else if (pts.length === 1 && vueRef.current.z > 1) {
      setVue((v) => ({ ...v, x: g.x + (e.clientX - g.cx), y: g.y + (e.clientY - g.cy) }))
    }
  }

  function haut(e: React.PointerEvent) {
    pointeurs.current.delete(e.pointerId)
    // Glissement horizontal en vue complète : photo précédente / suivante.
    const d = depart.current
    if (d && pointeurs.current.size === 0 && vueRef.current.z <= 1.01) {
      const dx = e.clientX - d.x
      const dy = e.clientY - d.y
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        if (dx < 0) suivante?.()
        else precedente?.()
      }
    }
    depart.current = null
    const reste = [...pointeurs.current.values()]
    const v = vueRef.current
    // Passe du pincement au déplacement sans saut.
    geste.current = reste.length === 1 ? { dist: 0, z: v.z, cx: reste[0].x, cy: reste[0].y, x: v.x, y: v.y } : null
  }

  function basculer(px: number, py: number) {
    if (vueRef.current.z > 1.05) setVue({ z: 1, x: 0, y: 0 })
    else zoomerVers(2.5, px, py)
  }

  const c = centre
  return (
    <Superpose className="visionneuse" role="dialog" aria-label="Photo en plein écran">
      <button className="fermer-x sur-noir" onClick={fermer} aria-label="Fermer" title="Fermer (Échap)">
        ✕
      </button>
      <div
        ref={cadre}
        className="visionneuse-cadre"
        onPointerDown={bas}
        onPointerMove={bouge}
        onPointerUp={haut}
        onPointerCancel={haut}
        onDoubleClick={(e) => { if (Date.now() - dernierToucher.current > 600) basculer(e.clientX, e.clientY) }}
        style={{ cursor: vue.z > 1 ? 'grab' : 'zoom-in' }}
      >
        <img
          src={url}
          alt=""
          draggable={false}
          style={{ transform: `translate(${vue.x}px, ${vue.y}px) scale(${vue.z})` }}
        />
      </div>

      <div className="visionneuse-barre">
        <button className="camera-txt" onClick={fermer}>
          Fermer
        </button>
        <span style={{ flex: 1 }} />
        <button
          className="pdf-btn visionneuse-btn"
          onClick={precedente}
          disabled={!precedente}
          aria-label="Photo précédente"
        >
          ‹
        </button>
        {position && (
          <span className="visionneuse-pct">
            {position.rang} / {position.total}
          </span>
        )}
        <button
          className="pdf-btn visionneuse-btn"
          onClick={suivante}
          disabled={!suivante}
          aria-label="Photo suivante"
        >
          ›
        </button>
        <span style={{ width: 14 }} />
        <button
          className="pdf-btn visionneuse-btn"
          onClick={() => zoomerVers(vue.z / 1.5, c().x, c().y)}
          disabled={vue.z <= MIN}
          aria-label="Zoom arrière"
        >
          −
        </button>
        <span className="visionneuse-pct">{Math.round(vue.z * 100)} %</span>
        <button
          className="pdf-btn visionneuse-btn"
          onClick={() => zoomerVers(vue.z * 1.5, c().x, c().y)}
          disabled={vue.z >= MAX}
          aria-label="Zoom avant"
        >
          +
        </button>
        <button
          className="pdf-btn visionneuse-btn"
          style={{ width: 'auto', padding: '0 10px', fontSize: 12 }}
          onClick={() => setVue({ z: 1, x: 0, y: 0 })}
          disabled={vue.z === 1}
        >
          Ajuster
        </button>
      </div>
    </Superpose>
  )
}
